import { useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { UploadCloud, FileText, Loader2, CheckCircle2, FileImage, FileType2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Upload() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("idle"); // idle | extracting | structuring | done
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  const submit = async () => {
    if (!file) { toast.error("Choose a resume file"); return; }
    setBusy(true); setStep("extracting");
    try {
      const fd = new FormData();
      fd.append("file", file);
      setTimeout(() => setStep("structuring"), 800);
      const r = await api.post("/resumes/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStep("done");
      toast.success("Resume standardized");
      setTimeout(() => navigate(`/editor/${r.data.id}`), 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
      setStep("idle");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold">Step 1 · Ingest</div>
      <h1 className="display text-3xl md:text-4xl font-black tracking-tighter mt-1">Drop a resume — get a recruiter-grade output.</h1>
      <p className="text-muted-foreground mt-2">Accepts PDF, DOCX or image. The AI extracts, rewrites and rebuilds it in your standard template.</p>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="resume-upload-zone"
        className={`mt-8 border-2 border-dashed rounded-xl p-10 md:p-14 text-center cursor-pointer lift transition-colors ${file ? "border-primary bg-accent/40" : "border-border hover:border-primary/60 hover:bg-secondary/40"}`}
      >
        <input ref={inputRef} type="file" accept=".pdf,.docx,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => setFile(e.target.files?.[0])} data-testid="resume-file-input" />
        {!file ? (
          <>
            <div className="mx-auto size-12 grid place-items-center rounded-lg bg-primary/10 text-primary"><UploadCloud className="size-6" /></div>
            <div className="display font-bold text-lg mt-4">Click or drag a resume here</div>
            <div className="text-sm text-muted-foreground mt-1">PDF · DOCX · PNG · JPG — up to 15MB</div>
            <div className="flex items-center justify-center gap-3 mt-5 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><FileText className="size-3.5" /> PDF</span>
              <span className="inline-flex items-center gap-1"><FileType2 className="size-3.5" /> DOCX</span>
              <span className="inline-flex items-center gap-1"><FileImage className="size-3.5" /> Image</span>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center gap-3">
            <div className="size-10 grid place-items-center rounded-md bg-primary/10 text-primary"><FileText className="size-5" /></div>
            <div className="text-left">
              <div className="font-semibold">{file.name}</div>
              <div className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} KB</div>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-6">
        <Button onClick={submit} disabled={!file || busy} size="lg" className="lift gap-2" data-testid="upload-submit-button">
          {busy ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
          {busy ? "Working…" : "Standardize with AI"}
        </Button>
        {file && !busy && (
          <Button variant="ghost" onClick={() => setFile(null)} data-testid="upload-clear-button">Clear</Button>
        )}
      </div>

      {busy && (
        <div className="mt-8 grid gap-2" data-testid="upload-progress">
          <Step label="Extracting text (PDF / DOCX / OCR)" active={step === "extracting"} done={["structuring","done"].includes(step)} />
          <Step label="Rewriting content with GPT-5.2 + mapping to template" active={step === "structuring"} done={step === "done"} />
          <Step label="Saving & opening editor" active={false} done={step === "done"} />
        </div>
      )}
    </div>
  );
}

function Step({ label, active, done }) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-md border ${done ? "border-primary/40 bg-accent/40" : active ? "border-primary/40 bg-card ai-pulse" : "border-border bg-card"}`}>
      {done ? <CheckCircle2 className="size-4 text-primary" /> : active ? <Loader2 className="size-4 animate-spin text-primary" /> : <div className="size-4 rounded-full border border-border" />}
      <div className="text-sm font-medium">{label}</div>
    </div>
  );
}
